import { atom } from 'jotai';

import { SummaryConfig } from '@repo/sdk/models/components';

import { EquipmentIdentifiers } from '@/features/equipment-browser/types';

// ============================================================================
// Pure Functions
// ============================================================================

/**
 * Helper function to calculate navigation IDs for circular navigation
 * @param equipmentId - Current equipment ID
 * @param equipmentPool - Array of available equipment
 * @returns Object with next and previous equipment IDs
 */
export const calculateNavigationIds = (
  equipmentId: string | undefined,
  equipmentPool: SummaryConfig[]
): { nextEquipmentId: string | undefined; prevEquipmentId: string | undefined } => {
  if (!equipmentId || !equipmentPool.length) {
    return { nextEquipmentId: undefined, prevEquipmentId: undefined };
  }

  const currentIndex = equipmentPool.findIndex((summary) => summary.referenceId === equipmentId);
  if (currentIndex === -1) {
    return { nextEquipmentId: undefined, prevEquipmentId: undefined };
  }

  const nextIndex = (currentIndex + 1) % equipmentPool.length;
  const prevIndex = (currentIndex - 1 + equipmentPool.length) % equipmentPool.length;

  return {
    nextEquipmentId: equipmentPool[nextIndex]?.referenceId,
    prevEquipmentId: equipmentPool[prevIndex]?.referenceId,
  };
};

export const identifiersAtom = atom<EquipmentIdentifiers | undefined>(undefined);
identifiersAtom.debugLabel = 'SelectedEquipmentState.identifiersAtom';
/** Current selected equipment ID */
export const equipmentIdAtom = atom<string | undefined>(undefined);
equipmentIdAtom.debugLabel = 'SelectedEquipmentState.equipmentIdAtom';

/** Pool of available equipment for navigation */
export const equipmentPoolAtom = atom<SummaryConfig[]>([]);
equipmentPoolAtom.debugLabel = 'SelectedEquipmentState.equipmentPoolAtom';

/**
 * Derived Atoms - Computed state
 */

/** Current equipment object derived from ID and pool */
export const equipmentAtom = atom<SummaryConfig | undefined>((get) => {
  const equipmentId = get(equipmentIdAtom);
  const equipmentPool = get(equipmentPoolAtom);

  if (!equipmentId) return undefined;

  return equipmentPool.find((summary) => summary.referenceId === equipmentId);
});
equipmentAtom.debugLabel = 'SelectedEquipmentState.equipmentAtom';

/** Next equipment ID for circular navigation */
export const nextEquipmentIdAtom = atom<string | undefined>((get) => {
  const equipmentId = get(equipmentIdAtom);
  const equipmentPool = get(equipmentPoolAtom);

  const { nextEquipmentId } = calculateNavigationIds(equipmentId, equipmentPool);
  return nextEquipmentId;
});
nextEquipmentIdAtom.debugLabel = 'SelectedEquipmentState.nextEquipmentIdAtom';

/** Previous equipment ID for circular navigation */
export const prevEquipmentIdAtom = atom<string | undefined>((get) => {
  const equipmentId = get(equipmentIdAtom);
  const equipmentPool = get(equipmentPoolAtom);

  const { prevEquipmentId } = calculateNavigationIds(equipmentId, equipmentPool);
  return prevEquipmentId;
});
prevEquipmentIdAtom.debugLabel = 'SelectedEquipmentState.prevEquipmentIdAtom';
